import type {
  SubmitResponse,
  SubmitStatus,
} from '../../graphql/mutation/submit';
import { useState, useEffect } from 'react';
import { Layout } from '../Layout/Layout';
import { queryClient } from '../../queryClient';
import { MUTATIONS } from '../../utils/constants';
import { RequestDetails } from '../RequestDetails/RequestDetails';
import { useWorkflow } from '../../workflow/hooks';
import { useLabelPolling } from './useLabelPolling';
import { getParams } from '../../utils/searchParams';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Page, PageWrap } from '../Page/Page';
import { Promo } from '../Promo/Promo';

// TODO: Replace with project wide date formatting
// TODO: Replace with actual dates
const date = new Date().toLocaleDateString('en-US', {
  month: 'short',
  day: '2-digit',
});

export const Complete = () => {
  const params = getParams();
  const isUnauthComplete = params?.complete === 'true';
  const navigate = useNavigate();
  const { retailer: retailerParam } = useParams({ strict: false });

  const submitResponse = queryClient.getQueryData<SubmitResponse>([
    MUTATIONS.SUBMIT,
  ]) as SubmitResponse;

  const submitInfo = submitResponse?.submit?.info?.[0];
  const cart = submitResponse?.submit?.cart;
  const email = submitResponse?.submit?.email;
  const label = submitInfo?.label;
  const deliveryMethod = cart?.delivery;
  const location = submitResponse?.submit?.cart?.location;

  const {
    order,
    orderNumber,
    config,
    retailer: workflowRetailer,
  } = useWorkflow();
  const retailer = retailerParam ?? workflowRetailer;
  const requestId = params?.request ?? submitResponse?.submit?.cart?.id;

  const [pollingEnabled, setPollingEnabled] = useState(
    !params?.shid && !isUnauthComplete,
  );

  const {
    shipmentData: { labelUrl, qrCodeUrl },
    status: pollingStatus,
  } = useLabelPolling({ requestId, enabled: pollingEnabled });

  useEffect(() => {
    if (pollingStatus === 'timeout') {
      setPollingEnabled(false);
    }
  }, [pollingStatus]);

  const derivedStatus: SubmitStatus = (() => {
    if (pollingStatus === 'success') {
      return 'REQUEST_COMPLETE';
    }
    if (submitResponse?.submit?.status) {
      return submitResponse.submit.status;
    }
    return 'REQUEST_RECEIVED';
  })();

  const replacementTokens = {
    orderNumber: orderNumber ?? submitInfo?.orderNumber ?? '',
    submittedDate: date,
    itemCount: `${cart?.items.length}`,
    deliveryMethodType: `${deliveryMethod?.type ?? ''}`,
    email: email ?? '',
  };

  const data = {
    email: email ?? undefined,
    requestId: requestId ?? undefined,
    label: { url: label?.url ?? labelUrl },
    qrCode: { url: qrCodeUrl },
    status: derivedStatus,
    deliveryMethod,
    deliveryType: deliveryMethod?.type,
    orderItems: order?.items ?? [],
    requestItems: cart?.items ?? [],
    location,
  };

  // Page includes authentication check; PageWrap does not
  const PageComponent = isUnauthComplete ? PageWrap : Page;

  const hasItemSelectionStep = config?.steps?.some(
    (step) => step.id === 'itemSelection',
  );

  const handleBack = () => {
    navigate({
      to: '/$retailer/item-selection',
      params: { retailer: retailer ?? '' },
      search: params ?? {},
      reloadDocument: true,
    });
  };

  return (
    <PageComponent>
      <Layout isFullWidth>
        <Promo id={'complete.pageTop'} />
        <RequestDetails
          headerTitle={{
            langKey: [
              `Global.confirmation.${derivedStatus}.${deliveryMethod?.type}.${deliveryMethod?.packagingOption}.header.title`,
              `Global.confirmation.${derivedStatus}.header.title`,
              `Global.confirmation.header.title`,
            ],
            replacementTokens,
          }}
          headerSubtitle={{
            langKey: [
              `Global.confirmation.${derivedStatus}.${deliveryMethod?.type}.${deliveryMethod?.packagingOption}.header.subtitle`,
              `Global.confirmation.${derivedStatus}.header.subtitle`,
              `Global.confirmation.header.subtitle`,
            ],
            replacementTokens,
          }}
          deliveryTitle={{
            langKey: [
              `Global.confirmation.${derivedStatus}.delivery.title.${[cart?.delivery?.type, cart?.delivery?.packagingOption].filter(Boolean).join('.')}`,
              `Global.confirmation.delivery.title.${[cart?.delivery?.type, cart?.delivery?.packagingOption].filter(Boolean).join('.')}`,
            ],
            replacementTokens,
          }}
          data={data}
          replacementTokens={replacementTokens}
          pollingStatus={pollingStatus}
          onBack={hasItemSelectionStep ? handleBack : undefined}
        >
          <Promo id={'complete.pageBottom'} />
        </RequestDetails>
      </Layout>
    </PageComponent>
  );
};
